import { useMemo, useState } from 'react';

/**
 * OutreachPricingExplorer — blog16, interactive pricing item.
 *
 * "Which plan fits my raise?" — the reader drags the reachouts slider and the
 * matching plan lights up with its per-reachout price, next to what the same
 * volume costs elsewhere. Data mirrors the single source of truth:
 * raisy/projects/blog/assets/blog16/raisy_outreach_infra_costs.csv (rows
 * "Raisy *" and "AiSDR Solo") — never retype numbers; update the CSV and this
 * mirror in the same pass (Rule 27/28).
 */

type Plan = {
  key: string;
  name: string;
  priceLabel: string;
  pricePerMonth: number;
  reachouts: number;
  unit: string;
  note: string;
  popular?: boolean;
};

const PLANS: Plan[] = [
  { key: 'free', name: 'Free', priceLabel: '$0', pricePerMonth: 0, reachouts: 5, unit: 'one-time', note: 'Your first raise, no card required' },
  { key: 'starter', name: 'Starter', priceLabel: '$49 / month', pricePerMonth: 49, reachouts: 250, unit: 'per month', note: 'Less than the DIY tools alone' },
  { key: 'growth', name: 'Growth', priceLabel: '$129 / month', pricePerMonth: 129, reachouts: 750, unit: 'per month', note: 'A full seed-round outreach month', popular: true },
  { key: 'scale', name: 'Scale', priceLabel: '$279 / month', pricePerMonth: 279, reachouts: 1700, unit: 'per month, every month', note: 'Continuous high-volume outreach — multi-geography or back-to-back rounds' },
];

const AISDR_200_USD = 250; // AiSDR Solo, 200 generic contacts/mo (CSV row "AiSDR Solo (monthly)")
const AGENCY_LOW_USD = 5000; // full-service agency retainer floor (CSV row "Fundraising agency retainer")

const planFor = (n: number): Plan => {
  if (n <= PLANS[0].reachouts) return PLANS[0];
  if (n <= PLANS[1].reachouts) return PLANS[1];
  if (n <= PLANS[2].reachouts) return PLANS[2];
  return PLANS[3];
};

const eur = (n: number) => `$${n.toLocaleString('en-IE')}`;
const perReachout = (p: Plan) => (p.reachouts > 0 ? Math.round((p.pricePerMonth * 100) / p.reachouts) / 100 : 0);

export const OutreachPricingExplorer = () => {
  const [volume, setVolume] = useState(200);
  const active = useMemo(() => planFor(volume), [volume]);

  const comparison = useMemo(() => {
    const raisy = active.pricePerMonth || 0;
    const aisdr = AISDR_200_USD;
    const agency = AGENCY_LOW_USD;
    const max = Math.max(agency, 1);
    const bar = (v: number) => `${Math.max((v / max) * 100, 2).toFixed(1)}%`;
    return [
      { label: 'Raisy', value: raisy, display: raisy === 0 ? '$0' : `${eur(raisy)} / month`, width: bar(raisy), color: 'bg-cyan-500' },
      { label: 'AiSDR Solo (generic sales)', value: aisdr, display: `$${AISDR_200_USD.toLocaleString()} / month`, width: bar(aisdr), color: 'bg-slate-400' },
      { label: 'Fundraising agency', value: agency, display: `$${AGENCY_LOW_USD.toLocaleString()}+ / month`, width: bar(agency), color: 'bg-slate-600' },
    ];
  }, [active]);

  return (
    <section className="my-6 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-4 sm:p-6">
      <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">Find your plan</h3>
      <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
        Drag the slider to your monthly outreach volume — the matching plan lights up.
      </p>

      {/* Slider */}
      <div className="mt-5">
        <label htmlFor="reachout-volume" className="text-sm font-medium text-slate-700 dark:text-slate-300">
          Investor reachouts per month: <span className="font-bold text-cyan-700 dark:text-cyan-400">{volume}</span>
        </label>
        <input
          id="reachout-volume"
          type="range"
          min={5}
          max={600}
          step={5}
          value={volume}
          onChange={(e) => setVolume(Number(e.target.value))}
          className="mt-2 h-8 w-full cursor-pointer accent-cyan-600"
          style={{ minHeight: '32px' }}
        />
        <div className="flex justify-between text-label-xs text-slate-600 dark:text-slate-500">
          <span>5</span><span>50</span><span>200</span><span>600</span>
        </div>
      </div>

      {/* Anchor — the alternative this replaces, made impossible to miss */}
      <div className="mt-5 rounded-lg border border-slate-300 dark:border-slate-600 bg-slate-100/70 dark:bg-slate-800/60 px-4 py-3">
        <div className="flex flex-wrap items-baseline justify-between gap-x-4">
          <span className="text-sm text-slate-600 dark:text-slate-400">
            The other way to get this done — a fundraising agency:
          </span>
          <span className="text-lg font-bold text-slate-500 dark:text-slate-400 line-through decoration-slate-400/70">
            $5,000–25,000 / month + success fees
          </span>
        </div>
      </div>

      {/* Plan cards */}
      <div className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
        {PLANS.map((p) => {          const isActive = p.key === active.key;
          return (
            <div
              key={p.key}
              className={`relative rounded-lg border p-4 transition-all ${
                isActive
                  ? 'border-cyan-500 ring-2 ring-cyan-500/40 bg-cyan-50/60 dark:bg-cyan-950/30'
                  : 'border-slate-200 dark:border-slate-700 bg-slate-50/50 dark:bg-slate-800/40'
              }`}
            >
              {p.popular && (
                <span className="absolute -top-2.5 left-3 rounded-full bg-cyan-600 px-2 py-0.5 text-label-2xs font-semibold uppercase tracking-wide text-white">
                  Most popular
                </span>
              )}
              <div className="text-sm font-semibold text-slate-900 dark:text-slate-100">{p.name}</div>
              <div className="mt-1 text-xl font-bold text-cyan-700 dark:text-cyan-400">
                {p.pricePerMonth > 0 ? `${eur(Number(perReachout(p).toFixed(2)))} / reachout` : '$0'}
              </div>
              <div className="text-xs text-slate-500 dark:text-slate-400">
                {p.pricePerMonth > 0 ? `${p.priceLabel} — up to ${p.reachouts} reachouts / month` : `${p.reachouts} reachouts total — your first raise, no card required`}
              </div>
              <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">{p.note}</p>
              {isActive && p.pricePerMonth > 0 && volume > 0 && (
                <div className="mt-2 rounded bg-cyan-100/70 dark:bg-cyan-900/40 px-2 py-1.5 text-label-xs font-medium text-cyan-800 dark:text-cyan-300">
                  Covers your {volume}/mo
                  {p.reachouts - volume > 0 ? ` — ${p.reachouts - volume} headroom included` : ''}
                  {' · '}
                  effective rate at your volume: {eur(Math.round((p.pricePerMonth * 100) / volume) / 100)} / reachout
                </div>
              )}
              {isActive && p.pricePerMonth === 0 && (
                <div className="mt-2 text-label-xs font-semibold uppercase tracking-wide text-cyan-700 dark:text-cyan-400">
                  Covers your {volume}/mo — free
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Premium curve — the pricing principle made visible */}
      <div className="mt-3 rounded-lg border border-slate-200 dark:border-slate-700 p-3">
        <div className="text-label-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">
          Our pricing principle: the premium tightens the more you reach out
        </div>
        <div className="mt-2 flex items-end gap-2">
          {[
            { label: 'Starter', rate: 0.196 },
            { label: 'Growth', rate: 0.172 },
            { label: 'Scale', rate: 0.164 },
          ].map((s) => (
            <div key={s.label} className="flex-1 text-center">
              <div className="text-xs font-bold text-cyan-700 dark:text-cyan-400">${s.rate.toFixed(2)}</div>
              <div
                className="mx-auto mt-1 w-full max-w-12 rounded-t bg-cyan-500/80"
                style={{ height: `${Math.round(s.rate * 60)}px` }}
              />
              <div className="mt-1 text-label-2xs text-slate-500 dark:text-slate-400">{s.label}</div>
            </div>
          ))}
        </div>
        <div className="mt-1 text-label-2xs text-slate-600 dark:text-slate-500">per reachout — lower is better for you</div>
      </div>

      {/* Comparison bars */}
      <div className="mt-6">
        <div className="text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">
          Same volume ({volume} reachouts/mo{volume > 200 ? ' — shown at the 200 tier' : ''}) elsewhere
        </div>
        <div className="mt-2 space-y-2">
          {comparison.map((c) => (
            <div key={c.label} className="flex items-center gap-3">
              <div className="w-40 shrink-0 truncate text-xs text-slate-600 dark:text-slate-400 sm:w-56">{c.label}</div>
              <div className="h-5 flex-1 rounded bg-slate-100 dark:bg-slate-800">
                <div className={`h-5 rounded ${c.color}`} style={{ width: c.width }} />
              </div>
              <div className="w-28 shrink-0 text-right text-xs font-medium text-slate-700 dark:text-slate-300">{c.display}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Named bonuses + two-layer guarantee */}
      <div className="mt-5 rounded-lg border border-emerald-300/60 dark:border-emerald-700/50 bg-emerald-50/60 dark:bg-emerald-950/20 p-3 text-center">
        <span className="text-sm font-semibold text-emerald-800 dark:text-emerald-300">
          No commissions, no equity, no hidden fees — ever. One number is published: a{' '}
          <strong>1% success fee</strong>, charged only when your round closes. Agencies take 1–6% of your round
          plus a $5,000–25,000 monthly retainer; we charge a flat monthly price, and the 1% only once it worked.
        </span>
      </div>
      <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div className="rounded-lg border border-emerald-300/60 dark:border-emerald-700/50 bg-emerald-50/60 dark:bg-emerald-950/20 p-4">
          <div className="text-xs font-semibold uppercase tracking-wide text-emerald-700 dark:text-emerald-400">
            Included in every plan
          </div>
          <ul className="mt-2 space-y-1.5 text-sm text-slate-700 dark:text-slate-300">
            <li><strong>The Compliance Shield</strong> — per-country gate on every send and every preview</li>
            <li><strong>Verified-or-Replaced</strong> — verified at send time; any hard bounce replaced free</li>
            <li><strong>Warm-Up Done-For-You</strong> — domains, mailboxes, and the 30–45 day clock, carried by us</li>
            <li><strong>The Evidence Trail</strong> — every send logged, auditable, yours</li>
          </ul>
        </div>
        <div className="rounded-lg border border-cyan-400/60 bg-cyan-50/60 dark:bg-cyan-950/25 dark:border-cyan-500/40 p-4">
          <div className="text-xs font-semibold uppercase tracking-wide text-cyan-700 dark:text-cyan-400">
            The First-Conversation Guarantee
          </div>
          <p className="mt-2 text-sm text-slate-700 dark:text-slate-300">
            On Growth and Scale: <strong>at least one investor response in your first 200 reachouts — or we keep
            sending for free until you get one.</strong> We can promise this because the addresses are verified, the
            gate keeps every email on the right side of the rules, and the list is ranked for fit.
          </p>
        </div>
      </div>

      {/* Honest urgency */}
      <p className="mt-4 text-center text-xs text-slate-500 dark:text-slate-400">
        We onboard a limited number of raises per month — warm infrastructure is real, not rented, and per-mailbox
        capacity is capped by the same compliance rules that protect your domain.
      </p>

      {/* Practical outcome — mirrors CSV rows "Cold outreach reply rate", "Meetings per 100 contacts", "Contact-to-investor conversion" */}
      {/* ONE published band for this product: the SAME one the table and the chart use (3–10% tailored
          cold outreach, midpoint 6.5%; meetings = 40% of replies). An earlier version published 2–5%
          here and 6.5% there — for the same product, in the same post. Two numbers for one fact is the
          defect class D-078 named for the unit; this is the estimate. */}
      <div className="mt-5 rounded-lg bg-slate-50 dark:bg-slate-800/60 p-4">
        <div className="text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">
          What {volume} reachouts/month realistically gets you
        </div>
        <div className="mt-2 grid grid-cols-2 gap-3 sm:grid-cols-4">
          <div>
            <div className="text-lg font-bold text-slate-900 dark:text-white">
              {Math.round(volume * 0.03)}–{Math.round(volume * 0.1)}
            </div>
            <div className="text-xs text-slate-500 dark:text-slate-400">replies / month (3–10% tailored band)</div>
          </div>
          <div>
            <div className="text-lg font-bold text-slate-900 dark:text-white">
              {Math.max(1, Math.round(volume * 0.03 * 0.4))}–{Math.max(1, Math.round(volume * 0.1 * 0.4))}
            </div>
            <div className="text-xs text-slate-500 dark:text-slate-400">meetings / month (40% of replies)</div>
          </div>
          <div>
            <div className="text-lg font-bold text-slate-900 dark:text-white">
              {(volume * 0.005).toFixed(1).replace('.0', '')}–{(volume * 0.01).toFixed(1).replace('.0', '')}
            </div>
            <div className="text-xs text-slate-500 dark:text-slate-400">investor commitments / month (0.5–1%)</div>
          </div>
          <div>
            <div className="text-lg font-bold text-slate-900 dark:text-white">
              {Math.max(1, Math.round((volume * 3) / 100))}–{Math.max(2, Math.round((volume * 9) / 100))}
            </div>
            <div className="text-xs text-slate-500 dark:text-slate-400">
              meetings over a 3-month raise — a realistic shot at 1–3 term-sheet discussions
            </div>
          </div>
        </div>
        <p className="mt-2 text-label-xs text-slate-600 dark:text-slate-500">
            Planning estimates, not promises, on the <strong>same band published in the table above</strong>:
            replies at 3–10% (tailored cold investor outreach — a fit-ranked list and an email written for
            each investor; 6.5% is its midpoint, which is the point estimate the comparison uses), meetings
            at 40% of replies, closure at the 0.5–1% contact-to-investor heuristic (founders typically speak
            with 50–100 investors before a round closes). We do not yet have our own measured reply rate;
            when we do, it replaces the band. The plan guarantees the pipeline, not the close.
        </p>
      </div>
    </section>
  );
};
